import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { navPath } from '@/constant';
import { CheckCircle, Loader2, PencilIcon, UserPlus, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { withRoleGuard } from '../../components/with-role-guard';
import ChangeTeamDialog from './components/change-team-dialog';
import CreateUserDialog from './components/create-user-dialog';
import ResetPasswordDialog from './components/reset-password-dialog';
import { CustomPagination } from '@/pages/dashboard/components/custom-pagination';

interface User {
  _id: string;
  username: string;
  team: string;
  active: boolean;
  role: string;
}

const UserPanel = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState<string | null>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isChangingTeam, setIsChangingTeam] = useState(false);
  const [newTeam, setNewTeam] = useState('');
  const [teamUsername, setTeamUsername] = useState('');
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/v1/user?page=${currentPage}`);
      const data = await response.json();

      setUsers(data.data.all_user_data);
      setTotalUsers(data.data.total.data);
      setTotalPages(data.data.total.page);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleActive = async (username: string) => {
    setUpdatingUsers(prev => new Set(prev).add(username));
    try {
      const response = await fetch('/api/v1/user/toggle-active', {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message);

      setUsers(prevUsers => prevUsers.map(user => (user.username === username ? { ...user, active: !user.active } : user)));

      toast.success(`User status updated successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Error updating user status');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(username);
        return newSet;
      });
    }
  };

  const handleChangeTeam = async () => {
    if (!newTeam.trim()) {
      toast.error('Team name cannot be empty');
      return;
    }

    setUpdatingUsers(prev => new Set(prev).add(teamUsername));
    try {
      const response = await fetch('/api/v1/user/change-team', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: teamUsername,
          team: newTeam,
        }),
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message);

      setUsers(prevUsers => prevUsers.map(user => (user.username === teamUsername ? { ...user, team: newTeam } : user)));

      setIsChangingTeam(false);
      setNewTeam('');
      toast.success(`Team updated successfully`);
    } catch (error: any) {
      toast.error(error.message || 'Error changing team');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(teamUsername);
        return newSet;
      });
    }
  };

  const toggleResetPasswordModal = (username: string | null = null) => {
    setSelectedUsername(username);
    setIsEditingUser(prev => !prev);
  };

  const toggleCreateUserModal = () => {
    setIsCreatingUser(prev => !prev);
  };

  const toggleChangeTeamModal = (username: string = '', team: string = '') => {
    setTeamUsername(username);
    setNewTeam(team);
    setIsChangingTeam(prev => !prev);
  };

  const handleUserCreated = () => {
    fetchUsers();
  };

  return (
    <div className='space-y-4'>
      <Card className='border-primary/20 shadow-sm shadow-primary/10 dark:bg-gray-900 dark:border-gray-800 dark:shadow-md'>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-4 border-b border-primary/20 dark:border-gray-800'>
          <CardTitle className='text-xl font-bold'>User Management</CardTitle>
          <div className='flex space-x-2'>
            <Button variant='default' size='icon' onClick={toggleCreateUserModal}>
              <UserPlus className='h-5 w-5' />
            </Button>
          </div>
        </CardHeader>
        <CardContent className='pt-4'>
          <div className='rounded-md border border-primary/20 dark:border-gray-800 dark:bg-gray-900'>
            <Table>
              <TableHeader className='bg-primary/5 dark:bg-gray-800'>
                <TableRow className='hover:bg-primary/5 dark:hover:bg-gray-800'>
                  <TableHead className='w-[25%] font-bold text-foreground dark:text-foreground'>Username</TableHead>
                  <TableHead className='w-[15%] text-center font-bold text-foreground dark:text-foreground'>Team</TableHead>
                  <TableHead className='w-[15%] text-center font-bold text-foreground dark:text-foreground'>Role</TableHead>
                  <TableHead className='w-[15%] text-center font-bold text-foreground dark:text-foreground'>Status</TableHead>
                  <TableHead className='w-[30%] text-center font-bold text-foreground dark:text-foreground'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className='dark:bg-gray-900'>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className='h-60'>
                      <div className='flex justify-center items-center h-full'>
                        <Loader2 className='h-8 w-8 animate-spin text-primary' />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center py-10 text-muted-foreground'>
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map(user => (
                    <TableRow key={user._id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>
                        <div className='text-center flex items-center justify-center gap-2'>
                          {updatingUsers.has(user.username) ? (
                            <div className='flex items-center gap-2'>
                              <Loader2 className='h-4 w-4 animate-spin' />
                              <span className='px-3 py-2 rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground'>
                                Updating...
                              </span>
                            </div>
                          ) : (
                            <>
                              <span className='px-3 py-2 rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground'>
                                {user.team}
                              </span>
                              <Button
                                variant='ghost'
                                size='icon'
                                className='h-7 w-7'
                                onClick={() => toggleChangeTeamModal(user.username, user.team)}
                                disabled={updatingUsers.has(user.username)}
                              >
                                <PencilIcon className='h-4 w-4' />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='text-center'>
                          <span className='px-3 py-2 rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground'>
                            {user.role || 'USER'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='text-center'>
                          {user.active ? (
                            <span className='px-3 py-2 rounded-full border border-green-400 bg-green-100 text-green-600 dark:border-green-500 dark:bg-green-500/20 dark:text-green-400'>
                              Active
                            </span>
                          ) : (
                            <span className='px-3 py-2 rounded-full border border-red-400 bg-red-100 text-red-600 dark:border-red-500 dark:bg-red-500/20 dark:text-red-400'>
                              Inactive
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex space-x-2 justify-center'>
                          <Button
                            className='hover:bg-primary/20 dark:hover:bg-primary/20'
                            variant='secondary'
                            size='sm'
                            onClick={() => toggleResetPasswordModal(user.username)}
                            disabled={updatingUsers.has(user.username)}
                          >
                            Reset Password
                          </Button>

                          <Button
                            className={
                              user.active
                                ? 'bg-red-100 text-red-600 hover:bg-red-200 w-[120px] dark:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/30 dark:border-red-500/40'
                                : 'bg-green-100 text-green-600 hover:bg-green-200 w-[120px] dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500/30 dark:border-green-500/40'
                            }
                            variant='outline'
                            size='sm'
                            onClick={() => handleToggleActive(user.username)}
                            disabled={updatingUsers.has(user.username)}
                          >
                            {updatingUsers.has(user.username) ? (
                              <Loader2 className='h-4 w-4 animate-spin mx-auto' />
                            ) : user.active ? (
                              <>
                                <XCircle className='h-4 w-4 mr-1' /> Deactivate
                              </>
                            ) : (
                              <>
                                <CheckCircle className='h-4 w-4 mr-1' /> Activate
                              </>
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!isLoading && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalUsers}
              onPageChange={setCurrentPage}
              itemsPerPage={10}
              showItemCount={true}
            />
          )}
        </CardContent>
      </Card>

      <ResetPasswordDialog
        selectedUsername={selectedUsername}
        setIsEditingUser={setIsEditingUser}
        isEditingUser={isEditingUser}
      />

      <CreateUserDialog setIsCreatingUser={setIsCreatingUser} isCreatingUser={isCreatingUser} onUserCreated={handleUserCreated} />

      <ChangeTeamDialog
        isOpen={isChangingTeam}
        username={teamUsername}
        initialTeam={newTeam}
        isUpdating={updatingUsers.has(teamUsername)}
        onOpenChange={setIsChangingTeam}
        onSave={handleChangeTeam}
        onTeamChange={setNewTeam}
        teamValue={newTeam}
      />
    </div>
  );
};

export default withRoleGuard(UserPanel, ['ADMIN', 'MASTER'], navPath.login);
